import { io, Socket } from 'socket.io-client'
import { Env } from '../env'
import { Platform } from 'react-native'

const urlHost = new URL(Env.SOCKET_URL || Env.API_URL)?.host

const host =
  process.env.NODE_ENV === 'development'
    ? 'ws://' + urlHost
    : 'wss://' + urlHost
const _socket = io(host, {
  autoConnect: true,
  transports: ['websocket'],
})

export type SocketStatus = 'connected' | 'disconnected'

type StatusCallback = (status: SocketStatus) => void

/**
 * Socket Class. Connect/disconnect/error are listened to only once; status is shared via subscribeStatus.
 */
export class SocketClient {
  private static socket: Socket = _socket
  private static status: SocketStatus = _socket.connected
    ? 'connected'
    : 'disconnected'
  private static statusListeners: Set<StatusCallback> = new Set()

  private static notifyStatus() {
    const s = this.status
    this.statusListeners.forEach((cb) => cb(s))
  }

  public static getStatus(): SocketStatus {
    return this.status
  }

  public static subscribeStatus(callback: StatusCallback): () => void {
    this.statusListeners.add(callback)
    callback(this.status)
    return () => {
      this.statusListeners.delete(callback)
    }
  }

  public static init(auth: {
    token: string
    userId: string
    username: string
    anonymous?: boolean
  }) {
    this.socket.auth = auth

    this.socket.on('connect', () => {
      console.info('Socket: Connected', Platform.OS)
      this.status = 'connected'
      this.notifyStatus()
    })
    this.socket.on('disconnect', () => {
      console.info('Socket: disconnect', Platform.OS)
      this.status = 'disconnected'
      this.notifyStatus()
    })
    this.socket.on('error', (error) => {
      console.info('Socket: Error', Platform.OS, error)
      this.status = 'disconnected'
      this.notifyStatus()
    })
    this.socket.on('connect_error', (error) =>
      console.info('Socket: Connect Error', Platform.OS, error)
    )
    this.socket.on('connect_timeout', () =>
      console.info('Socket: Connect Timeout')
    )
    this.socket.connect()
  }

  public static disconnect() {
    if (!this.socket) {
      return
    }

    this.socket.disconnect()
  }

  public static get() {
    return this.socket as Socket
  }
}
